import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, InputNumber, DatePicker, Button, Tag, Empty, Divider, Form, Select } from 'antd';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import Icons from '@/components/third-party/Icons';
import PageModal from '@/components/PageModal';
import DataTable from '@/components/DataTable';
import { get_demand_lots_list, create_demand_lots } from '@/utils/api';
import { fmtDate, lotStatusTag } from './helpers';
import BatchPurposeField from '@/components/BatchPurposeField';

// ==============================|| CREATE / VIEW DEMAND LOTS MODAL ||============================== //

const emptyRow = () => ({ key: Date.now() + Math.random(), lot_quantity: null, comment: '', lot_dates: null });

const CreateLotsModal = ({ open, onClose, demand, onLotsChanged, canCreate = true }) => {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  // CPV-01 DD-CPV-21: what these lots are for — required; options decided by the server for this record.
  const [purpose, setPurpose] = useState(undefined);
  const [purposeTouched, setPurposeTouched] = useState(false);
  // The market the purpose is checked with — offered from the record's own (server) markets, sent with
  // the lots so the issue checks exactly what the options were asked for.
  const [market, setMarket] = useState(undefined);
  const [markets, setMarkets] = useState([]);

  const fetchLots = () => {
    if (!demand?.demand_id) return;
    setLoading(true);
    get_demand_lots_list(demand.demand_id, (data) => {
      setLoading(false);
      if (data?.success) setLots(data.data || []);
    });
  };

  useEffect(() => {
    if (open) {
      fetchLots();
      setRows([emptyRow()]);
      setPurpose(undefined);
      setPurposeTouched(false);
      setMarket(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, demand?.demand_id]);

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (key) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));
  const updateRow = (key, field, value) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));

  const existingLotTotal = lots.reduce((sum, l) => sum + (Number(l.lot_quantity) || 0), 0);
  const newRowsTotal = rows.reduce((sum, r) => sum + (Number(r.lot_quantity) || 0), 0);
  const runningTotal = existingLotTotal + newRowsTotal;
  const requiredQuantity = Number(demand?.required_quantity) || 0;
  const overQuantity = runningTotal > requiredQuantity;

  // Each lot runs on the line sequentially — a new lot can't start before the previous
  // one (existing or newly-added-above) ends, and every lot must fall inside the
  // demand's own window. Neither is enforced server-side yet (see the "Backend needed"
  // note) — this is a client-side safety net only.
  const priorLotEndDates = lots.map((l) => l.lot_end_date).filter(Boolean).map((d) => dayjs(d));

  const getMinStartForRow = (rowIndex) => {
    const priorRowEndDates = rows
      .slice(0, rowIndex)
      .map((r) => r.lot_dates?.[1])
      .filter(Boolean);
    const allPriorEnds = [...priorLotEndDates, ...priorRowEndDates];
    const latestPriorEnd = allPriorEnds.reduce(
      (max, d) => (!max || d.isAfter(max) ? d : max),
      null
    );
    const demandStart = demand?.start_date ? dayjs(demand.start_date) : null;
    if (latestPriorEnd && demandStart) return latestPriorEnd.isAfter(demandStart) ? latestPriorEnd : demandStart;
    return latestPriorEnd || demandStart;
  };

  const handleSubmit = () => {
    const validRows = rows.filter((r) => Number(r.lot_quantity) > 0);
    if (validRows.length === 0) {
      toast.error('Add at least one lot with a quantity greater than 0');
      return;
    }
    if (validRows.some((r) => !r.lot_dates)) {
      toast.error('Select a start and end date for every lot');
      return;
    }
    // Server doesn't enforce this today — a UI-only safety net so lots can't silently
    // exceed what the demand actually asked for.
    if (overQuantity) {
      toast.error(`Total lot quantity (${runningTotal}) exceeds required quantity (${requiredQuantity})`);
      return;
    }

    setSubmitting(true);
    create_demand_lots(
      {
        demand_id: demand.demand_id,
        total_batch_quantity: requiredQuantity,
        batch_purpose: purpose,
        ...(market ? { market } : {}),
        lot_list: validRows.map((r) => ({
          lot_quantity: r.lot_quantity,
          comment: r.comment || undefined,
          lot_start_date: r.lot_dates[0].format('YYYY-MM-DD'),
          lot_end_date: r.lot_dates[1].format('YYYY-MM-DD'),
        })),
      },
      (data) => {
        setSubmitting(false);
        if (data?.success) {
          toast.success(`${data.lots_created} lot(s) created`);
          setRows([emptyRow()]);
          fetchLots();
          onLotsChanged?.();
        } else {
          toast.error(data?.message || 'Failed to create lots');
        }
      },
      { onError: () => setSubmitting(false) }
    );
  };

  const lotColumns = [
    { title: 'Lot Number', dataIndex: 'lot_number', key: 'lot_number' },
    { title: 'Batch Number', dataIndex: 'batch_number', key: 'batch_number' },
    { title: 'Lot Quantity', dataIndex: 'lot_quantity', key: 'lot_quantity' },
    { title: 'Total Batch Quantity', dataIndex: 'total_batch_quantity', key: 'total_batch_quantity' },
    { title: 'Start Date', dataIndex: 'lot_start_date', key: 'lot_start_date', render: fmtDate },
    { title: 'End Date', dataIndex: 'lot_end_date', key: 'lot_end_date', render: fmtDate },
    { title: 'Comment', dataIndex: 'comment', key: 'comment', render: (v) => v || '—' },
    { title: 'Status', key: 'status', render: () => lotStatusTag() },
  ];

  return (
    <PageModal
      icon={<Icons.Layers size={22} />}
      title={`Lots — ${demand?.batch_number || ''}`}
      description="Batches created from this master demand."
      open={open}
      onCancel={onClose}
      width={1140}
      destroyOnHidden
    >
      <div className="flex flex-col gap-4">
        <DataTable
          bordered
          pagination={false}
          columnConfig={false}
          rowKey="id"
          loading={loading}
          columns={lotColumns}
          data={lots}
          height={null}
          locale={{ emptyText: <Empty description="No lots created yet" /> }}
        />

        {canCreate && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <div className="flex items-center justify-between">
              <span className="font-medium">Add Lots</span>
              <Tag color={overQuantity ? 'error' : 'default'}>
                {runningTotal} / {requiredQuantity} required
              </Tag>
            </div>

            <Form.Item label="Market">
              <Select allowClear value={market} onChange={setMarket} options={markets.map((m) => ({ value: m, label: m }))}
                disabled={!markets.length} placeholder={markets.length ? 'Market (optional)' : 'No markets on this record'} />
            </Form.Item>
            <BatchPurposeField
              recipeId={demand?.recipe_id}
              market={market}
              onMarkets={setMarkets}
              value={purpose}
              onChange={(v) => { setPurpose(v); setPurposeTouched(true); }}
              touched={purposeTouched}
            />

            <div className="flex flex-col gap-2">
              {rows.map((row, index) => {
                const minStart = getMinStartForRow(index);
                return (
                  <div key={row.key} className="flex items-center gap-2">
                    <InputNumber
                      style={{ width: 140 }}
                      min={0.01}
                      placeholder="Lot Quantity"
                      value={row.lot_quantity}
                      onChange={(v) => updateRow(row.key, 'lot_quantity', v)}
                    />
                    <DatePicker.RangePicker
                      style={{ width: 340 }}
                      value={row.lot_dates}
                      onChange={(v) => updateRow(row.key, 'lot_dates', v)}
                      disabledDate={(current, { from } = {}) => {
                        if (minStart && current < minStart.startOf('day')) return true;
                        if (demand?.end_date && current > dayjs(demand.end_date).endOf('day')) return true;
                        if (from) return current && current < from.startOf('day');
                        return false;
                      }}
                    />
                    <Input
                      className="flex-1"
                      placeholder="Comment (optional)"
                      value={row.comment}
                      onChange={(e) => updateRow(row.key, 'comment', e.target.value)}
                    />
                    <Icons.Trash2
                      className="text-red-600 w-5 h-5 cursor-pointer hover:text-red-800"
                      onClick={() => removeRow(row.key)}
                    />
                  </div>
                );
              })}
            </div>

            <Button type="dashed" icon={<Icons.Plus className="w-4 h-4" />} onClick={addRow}>
              Add another lot
            </Button>

            <div className="flex justify-end">
              <Button type="primary" loading={submitting} onClick={handleSubmit}>
                Create Batches
              </Button>
            </div>
          </>
        )}
      </div>
    </PageModal>
  );
};

CreateLotsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  demand: PropTypes.object,
  onLotsChanged: PropTypes.func,
  canCreate: PropTypes.bool,
};

export default CreateLotsModal;
